import React from 'react';
import { useConfig } from '../context/ConfigContext';
import { formatDueDayOptions } from '../utils/formatters';

function TermsOfService() {
  const { getCompanyLegalName, getCompanyEmail, getCompanyPhone, getCurePeriodDays, getGracePeriodDays, getPaymentDueDayOptions } = useConfig();

  return (
    <div className="marketing-legal">
      {/* Hero Section */}
      <section className="marketing-hero marketing-hero-short">
        <div className="marketing-hero-content">
          <h1 className="marketing-hero-title">Terms of Service</h1>
          <p className="marketing-hero-subtitle">
            Last Updated: November 15, 2025
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="marketing-legal-content">
        <div className="container">
          <div className="legal-container">

            {/* Agreement */}
            <div className="legal-section">
              <h2>Agreement to Terms</h2>
              <p>
                Welcome to {getCompanyLegalName()} ("Company," "we," "our," or "us"). These Terms of Service
                ("Terms") govern your use of our website and services. By accessing or using our services, you agree to
                be bound by these Terms.
              </p>
              <div className="legal-warning">
                <strong>Important:</strong> Please read these Terms carefully before using our services. If you do not
                agree with these Terms, you must not use our website or services.
              </div>
            </div>

            {/* Eligibility */}
            <div className="legal-section">
              <h2>Eligibility</h2>
              <p>
                To use our services, you must:
              </p>
              <ul>
                <li>Be at least 18 years of age</li>
                <li>Have the legal capacity to enter into binding contracts</li>
                <li>Provide accurate and complete information during registration</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </div>

            {/* Account Registration */}
            <div className="legal-section">
              <h2>Account Registration and Security</h2>
              <p>
                When creating an account, you agree to:
              </p>
              <ul>
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Notify us immediately of any unauthorized access to your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
              <p>
                We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
              </p>
            </div>

            {/* Land Purchase and Financing */}
            <div className="legal-section">
              <h2>Land Purchase and Financing Terms</h2>

              <h3>Contract for Deed</h3>
              <p>
                All land purchases are made through a Contract for Deed, also known as a Land Contract. This means:
              </p>
              <ul>
                <li>Legal title remains with {getCompanyLegalName()} until the contract is paid in full</li>
                <li>You receive equitable title and can use the property according to the contract terms</li>
                <li>Upon full payment, we will transfer legal title via Special Warranty deed or Quitclaim deed</li>
                <li>Default on payments may result in forfeiture of all payments made and loss of property rights</li>
              </ul>

              <h3>Payment Terms</h3>
              <p>
                By purchasing property through our financing:
              </p>
              <ul>
                <li>You agree to make timely monthly payments on the {formatDueDayOptions(getPaymentDueDayOptions())} of each month (as selected)</li>
                <li>Late payments ({getGracePeriodDays()}+ days past due) incur a late fee as specified in your contract</li>
                <li>Payments include principal, interest, and may include property taxes and HOA fees</li>
                <li>You may prepay without penalty at any time</li>
                <li>A processing fee applies to all purchases and is non-refundable</li>
              </ul>

              <h3>Property Taxes and HOA Fees</h3>
              <p>
                For properties with tax escrow or HOA fees:
              </p>
              <ul>
                <li>Monthly payments include estimated property taxes and/or HOA fees</li>
                <li>We will pay property taxes and HOA fees on your behalf from escrowed funds</li>
                <li>You remain ultimately responsible for all property taxes and fees</li>
                <li>Escrow amounts may be adjusted annually based on actual tax bills</li>
              </ul>
            </div>

            {/* Default and Forfeiture */}
            <div className="legal-section legal-danger">
              <h2>Default and Forfeiture</h2>
              <p>
                You will be in default if:
              </p>
              <ul>
                <li>You fail to make a payment within the grace period</li>
                <li>You violate any other material term of your contract</li>
              </ul>
              <p>
                <strong>Cure Period:</strong> Upon default, you will receive a written notice providing {getCurePeriodDays()} days to cure
                the default by paying all amounts due plus applicable fees.
              </p>
              <p>
                <strong>If default is not cured within the cure period, we may declare the contract forfeit, resulting in:</strong>
              </p>
              <ul>
                <li>Forfeiture of all payments made (considered rent for use of property)</li>
                <li>Immediate loss of all rights to the property</li>
                <li>Requirement to vacate the property</li>
                <li>No refund of any payments or down payment</li>
              </ul>
            </div>

            {/* Property Representations */}
            <div className="legal-section">
              <h2>Property Representations and Warranties</h2>
              <p>
                <strong>PROPERTIES ARE SOLD "AS IS" WITHOUT WARRANTY OF ANY KIND.</strong>
              </p>
              <p>
                We make no representations or warranties regarding:
              </p>
              <ul>
                <li>Property condition, fitness for a particular purpose, or merchantability</li>
                <li>Access to utilities, roads, or services</li>
                <li>Buildability, soil conditions, or development potential</li>
                <li>Zoning, environmental conditions, or regulatory restrictions</li>
                <li>Boundaries, surveys, or exact acreage (approximations may vary)</li>
              </ul>
              <p>
                <strong>Due Diligence:</strong> You are responsible for conducting your own due diligence, including
                property inspections, title searches, and verification of all property information before purchase.
              </p>
            </div>

            {/* Use of Website */}
            <div className="legal-section">
              <h2>Acceptable Use of Website</h2>
              <p>
                You agree NOT to:
              </p>
              <ul>
                <li>Use the website for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to any part of the website</li>
                <li>Interfere with or disrupt the website or servers</li>
                <li>Transmit viruses, malware, or harmful code</li>
                <li>Scrape, harvest, or collect user information</li>
                <li>Impersonate any person or entity</li>
                <li>Submit false or misleading information</li>
              </ul>
            </div>

            {/* Intellectual Property */}
            <div className="legal-section">
              <h2>Intellectual Property</h2>
              <p>
                All content on this website, including text, graphics, logos, images, and software, is the property of
                {getCompanyLegalName()} and protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                You may not reproduce, distribute, modify, or create derivative works without our express written permission.
              </p>
            </div>

            {/* Payment Processing */}
            <div className="legal-section">
              <h2>Payment Processing</h2>
              <p>
                All payments are processed securely through Square, Inc. By making a payment, you agree to:
              </p>
              <ul>
                <li>Pay a convenience fee for online payments</li>
                <li>Provide accurate payment information</li>
                <li>Authorize us to charge your payment method for scheduled payments</li>
                <li>Square's terms of service and privacy policy</li>
              </ul>
              <p>
                We reserve the right to modify payment methods and fees with 30 days' notice.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="legal-section">
              <h2>Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, {getCompanyLegalName().toUpperCase()} SHALL NOT BE LIABLE FOR:
              </p>
              <ul>
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or use</li>
                <li>Property damage or personal injury</li>
                <li>Errors or interruptions in service</li>
              </ul>
              <p>
                Our total liability shall not exceed the total amount paid by you to us in the 12 months preceding the claim.
              </p>
            </div>

            {/* Dispute Resolution */}
            <div className="legal-section">
              <h2>Dispute Resolution and Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Wisconsin,
                without regard to conflict of law principles.
              </p>
              <p>
                Any disputes arising from these Terms or our services shall be resolved exclusively in the courts
                of Outagamie County, Appleton, Wisconsin. You consent to the personal jurisdiction and venue of
                such courts.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="legal-section">
              <h2>Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon
                posting to this page. Your continued use of our services after changes constitutes acceptance of the
                modified Terms. We will update the "Last Updated" date to reflect changes.
              </p>
            </div>

            {/* Severability */}
            <div className="legal-section">
              <h2>Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be
                limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain
                in full force and effect.
              </p>
            </div>

            {/* Contact */}
            <div className="legal-section legal-contact">
              <h2>Contact Us</h2>
              <p>
                For questions about these Terms of Service, please contact us:
              </p>
              <p>
                <strong>Email:</strong> {getCompanyEmail()}
              </p>
              <p>
                <strong>Phone:</strong> {getCompanyPhone()}
              </p>
              <p>
                <strong>{getCompanyLegalName()}</strong>
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export default TermsOfService;
